import React from 'react'
import { useTranslation } from 'react-i18next'
import { utcToZonedTime } from 'date-fns-tz'

import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { StateSchema } from '@/hooks/form/models'
import { ExpirationType, TimeZoneUTCType } from '@/enums'

import { sentenceCase } from '@/utils'
import { DatePickerCustomComponent } from '@/views/components'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
  required?: boolean
}

const ExpirationComponent = ({
  handleOnChange,
  state,
  required = false,
}: Props) => {
  const { t } = useTranslation()

  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleOnChange('expirationType', event.target.value)
  }

  const handleChangeDate = (date: any) => {
    handleOnChange('expirationDate', date)
  }

  const currentDate: Date = new Date()

  const todayZoned = utcToZonedTime(
    currentDate,
    TimeZoneUTCType['GMT+00 Coordinated Universal Time'],
  )

  return (
    <>
      <Box maxWidth={400}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontWeight={700} sx={{ mb: '15px' }}>
              {`${t('labels.expirationTime')}${required ? ' *' : ''}`}
            </Typography>
            <FormControl margin="dense">
              <RadioGroup
                row
                aria-label="expirationType"
                name="expirationType"
                value={state.expirationType.value}
                onChange={handleChangeType}
              >
                <>
                  <FormControlLabel
                    value={ExpirationType.TIME_UNIT}
                    control={<Radio color="primary" />}
                    label={t('labels.timeUnit')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={ExpirationType.SPECIFIC_DATE}
                    control={<Radio color="primary" />}
                    label={t('labels.specificDate')}
                    labelPlacement="end"
                  />
                </>
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      {state.expirationType.value === ExpirationType.TIME_UNIT ? (
        <Box maxWidth={400}>
          <TextField
            fullWidth
            name="expirationUnit"
            margin="none"
            variant="outlined"
            label={t('expirationTimeUnit')}
            required={required}
            error={
              state.expirationUnit.error.length > 0 ||
              (required && !state.expirationUnit.value)
            }
            helperText={
              required && !state.expirationUnit.value
                ? sentenceCase(`${t('error.requiredField')}`, true)
                : sentenceCase(state.expirationUnit.error, true)
            }
            value={state.expirationUnit.value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleOnChange(event.target.name, event.target.value)
            }
            InputProps={{
              endAdornment: (
                <Box pr={1}>
                  <Typography variant="body2" component="p">
                    {t('unitTime.days')}
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      ) : (
        <Box maxWidth={400}>
          <DatePickerCustomComponent
            label={t('labels.date')}
            value={state.expirationDate.value || null}
            onChange={(date) => {
              date !== undefined && date !== null && handleChangeDate(date)
            }}
            minDate={todayZoned}
            dayOfWeekFormatter={(day) => day}
          />
        </Box>
      )}
    </>
  )
}

export default ExpirationComponent
